// ADR 25.9.26k §1 — what the console's gate reads, assembled in one place.
//
// `null` while anything is still loading. Backend 25.9.26n §12 (O5) loads its
// grant fact here — the one place that knows what "still loading" means — so
// the route gates, the header's switch and the `/account` door all widen with
// no edit of their own.
//
// O5's cost decision: the list is fetched **whenever the hook runs**, with or
// without `enable_oauth_clients` — the gate needs it when the flag is off, the
// Connected-apps page needs it when it is on — under the one key the page
// reads, so the two make one request, and a 60 s `staleTime` keeps a walk
// between `/account` and `/admin` from repeating it. A failed read counts as
// no grant: the gate falls back to the flag (display only; the backend's
// revoke route stays open). The route takes `VerifiedClaims`, so a
// signed-out or unverified session is answered `{ hasLiveGrant: false }`
// without a request rather than with a refusal it would cost to produce.

import { useQuery } from '@tanstack/react-query'

import type { FeatureFlags } from '../../api/auth'
import { useAuthSession } from '../../auth/session'
import { listGrants, OAUTH_GRANTS_QUERY_KEY, OAUTH_GRANTS_STALE_MS } from './connectedApps/connectedApps'
import type { AdminFacts } from './sections'

export function useAdminFacts(flags: FeatureFlags | null): AdminFacts | null {
  const { status, emailVerified } = useAuthSession()
  const enabled = flags !== null && status === 'signedIn' && emailVerified
  const grants = useQuery({
    queryKey: OAUTH_GRANTS_QUERY_KEY,
    queryFn: listGrants,
    enabled,
    staleTime: OAUTH_GRANTS_STALE_MS,
    retry: false,
    // A failed read is an answer ("no grant"), not a loading state: re-fetching it
    // whenever another observer mounts would flip this hook back to `null`.
    retryOnMount: false,
  })
  if (flags === null) return null
  if (!enabled) return { flags, hasLiveGrant: false }
  if (grants.isPending) return null
  return { flags, hasLiveGrant: grants.isSuccess && grants.data.length > 0 }
}
